/**
 * RTK Query endpoints for message management via the agents API.
 * Handles sending messages and fetching message history.
 */

import type {
  MessageListParams,
  MessageListResponse,
  SendChatMessageRequest,
  SendChatMessageResponse,
} from '../interfaces/message.interface'
import { agentsApiSlice } from './agents-api.store'

type MessageTagType = 'Messages' | 'Conversations'
type MessageTag =
  | { type: 'Messages'; id: string }
  | { type: 'Agencies'; id: string }
  | MessageTagType

const CONVERSATIONS_API = '/api/v1/conversations'
const CHAT_API = '/api/v1/chat'

interface GetMessagesParams {
  conversationId: string
  params?: MessageListParams
}

const buildParams = (params?: MessageListParams): Record<string, number | boolean> | undefined =>
  params
    ? {
        page: params.page ?? 1,
        page_size: params.page_size ?? 50,
        include_notes: params.include_notes ?? true,
        include_internal: params.include_internal ?? true,
      }
    : undefined

const buildMessagesTags = (
  response: MessageListResponse | undefined,
  conversationId: string
): MessageTag[] =>
  response ? [{ type: 'Messages', id: conversationId }] : [{ type: 'Messages', id: conversationId }]

export const messageAgentsEndpoints = agentsApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAgentMessages: builder.query<MessageListResponse, GetMessagesParams>({
      query: ({ conversationId, params }) => ({
        url: `${CONVERSATIONS_API}/${conversationId}/messages`,
        params: buildParams(params),
      }),
      providesTags: (response, _, { conversationId }) =>
        buildMessagesTags(response, conversationId),
    }),

    sendAgentChatMessage: builder.mutation<SendChatMessageResponse, SendChatMessageRequest>({
      query: (body) => ({
        url: CHAT_API,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) => {
        if (!result) return []

        const tagList: MessageTag[] = [
          { type: 'Messages', id: result.conversation_id },
          { type: 'Agencies', id: 'RECENTLY_USED' },
        ]

        if (result.is_new_conversation) {
          tagList.push('Conversations')
        }

        return tagList
      },
    }),
  }),
})

export const { useGetAgentMessagesQuery, useSendAgentChatMessageMutation } = messageAgentsEndpoints
